import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { X, ChevronRight } from '../../icons';
import { Avatar } from '../../components/shared';
import type { TaskProjectDto, TaskDto } from '../../api/tasks';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { useAuthStore } from '../../store/authStore';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import { tTask } from '../../lib/task-i18n';
import './ProjectInfoPanel.css';

interface ProjectInfoPanelProps {
  project: TaskProjectDto;
  /** 이 프로젝트의 태스크 — 활동 내역과 진행률을 여기서 뽑는다(추가 조회 없음). */
  tasks: TaskDto[];
  onClose: () => void;
  /** 활동에서 업무를 누르면 상세로 — 설계서의 "작업을 클릭하면 상세내역 보인다". */
  onOpenTask: (task: TaskDto) => void;
}

type Tab = 'overview' | 'members' | 'activity';

/**
 * ProjectInfoPanel (화면구성도 V0.1 슬라이드 9, 2026-08-18) — 보드 우측 **프로젝트 정보** 패널.
 *
 * 탭 세 개(개요 · 멤버 · 활동 내역). 제안의 핵심은 활동 내역이다 —
 * "활동 내역에서 작업을 클릭하면 상세내역이 보인다".
 *
 * ⚠ 활동 로그 테이블은 아직 없다. 그래서 활동 내역은 **이미 받아 둔 태스크에서 파생**한다:
 *   태스크 생성(createdAt·createdBy) + 프로젝트 생성. 수정·상태 변경 이력은 서버가 남기지
 *   않으므로 여기 없다 — 없는 이력을 있는 척 그리지 않으려고 생성 이벤트만 보여 준다.
 */
export function ProjectInfoPanel({ project, tasks, onClose, onOpenTask }: ProjectInfoPanelProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const [tab, setTab] = useState<Tab>('overview');
  const [members, setMembers] = useState<TeamMemberDto[]>([]);
  const authUser = useAuthStore((s) => s.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    if (!project.teamId) { setMembers([]); return; }
    void listTeamMembers(project.teamId)
      .then((list) => { if (alive) setMembers(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [project.teamId]);

  const nameOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const x of members) m[x.userId] = x.name;
    for (const p of (project.memberPreview ?? [])) m[p.userId] = p.name;
    if (myId) m[myId] = authUser?.name ?? m[myId] ?? '';
    return m;
  }, [members, project.memberPreview, myId, authUser]);

  const done = tasks.filter((t) => t.status === 'DONE').length;
  const pct = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0;

  /** 활동 내역 — 태스크 생성 최신순 + 맨 끝에 프로젝트 생성. */
  const activity = useMemo(() => {
    const rows = [...tasks]
      .filter((t) => !!t.createdAt)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
      .slice(0, 50)
      .map((t) => ({ kind: 'task' as const, at: t.createdAt, actorId: t.createdBy, task: t }));
    return rows;
  }, [tasks]);

  const memberList: Array<{ userId: string; name: string; avatarUrl: string | null; sub?: string | null }> =
    members.length > 0
      ? members.map((m) => ({ userId: m.userId, name: m.name, avatarUrl: m.avatarUrl, sub: m.email }))
      : (project.memberPreview ?? []).map((p) => ({ userId: p.userId, name: p.name, avatarUrl: p.avatarUrl }));

  return (
    <>
      <div className="pip-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="pip-panel" role="dialog" aria-modal="true" aria-label={tProject('project.info.open')}>
        <header className="pip-head">
          <h2>{tProject('project.info.open')}</h2>
          <button type="button" className="pip-close" onClick={onClose} aria-label={tTask('task.btn.close')} title={tTask('task.btn.close')}>
            <X size={15} aria-hidden />
          </button>
        </header>

        <div className="pip-tabs" role="tablist">
          {([
            ['overview', tProject('project.info.tab_overview')],
            ['members', tProject('project.info.tab_members')],
            ['activity', tProject('project.info.tab_activity')],
          ] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              className={`pip-tab${tab === k ? ' pip-tab--on' : ''}`}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="pip-body scrollbar-thin">
          {tab === 'overview' && (
            <>
              <div className="pip-title-row">
                <span className="pip-dot" style={{ background: project.color ?? 'var(--text-muted)' }} aria-hidden />
                <b>{project.name}</b>
              </div>

              <div className="pip-field">
                <span className="pip-label">{tProject('project.info.desc')}</span>
                <p className={project.description ? 'pip-desc' : 'pip-desc pip-desc--empty'}>
                  {project.description || tProject('project.info.no_desc')}
                </p>
              </div>

              <div className="pip-field">
                <span className="pip-label">{tProject('project.info.team')}</span>
                <p className="pip-value">{project.teamName ?? tTask('task.topbar.personal')}</p>
              </div>

              {project.dueDate && (
                <div className="pip-field">
                  <span className="pip-label">{tProject('project.info.due')}</span>
                  <p className="pip-value">{project.dueDate.replace(/-/g, '.')}</p>
                </div>
              )}

              <div className="pip-field">
                <span className="pip-label">{tProject('project.info.progress')}</span>
                <div className="pip-bar" role="img" aria-label={`${pct}%`}>
                  <span className="pip-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <p className="pip-value pip-value--sub">
                  {tProject('project.info.counts', { done: String(done), total: String(tasks.length) })} · {pct}%
                </p>
              </div>
            </>
          )}

          {tab === 'members' && (
            <ul className="pip-members">
              {memberList.length === 0 && <li className="pip-empty">{tProject('project.info.no_members')}</li>}
              {memberList.map((m) => (
                <li key={m.userId} className="pip-member">
                  <Avatar userId={Number(m.userId) || 0} name={m.name} src={m.avatarUrl} size="sm" />
                  <span className="pip-member-body">
                    <b>{m.name}{m.userId === myId ? ` · ${tTask('task.teamdrop.me')}` : ''}</b>
                    {m.sub && <small>{m.sub}</small>}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {tab === 'activity' && (
            <>
              <p className="pip-hint">{tProject('project.info.act_hint')}</p>
              <ul className="pip-acts">
                {activity.length === 0 && <li className="pip-empty">{tProject('project.info.act_empty')}</li>}
                {activity.map((a) => (
                  <li key={a.task.id} className="pip-act">
                    <Avatar userId={Number(a.actorId) || 0} name={nameOf[a.actorId] ?? ''} src={null} size="xs" />
                    <span className="pip-act-body">
                      <span className="pip-act-line">
                        {tProject('project.info.act_task_created', { name: nameOf[a.actorId] ?? tTask('task.topbar.personal') })}
                        <em className="pip-act-at">{shortDateTime(a.at)}</em>
                      </span>
                      <button type="button" className="pip-act-task" onClick={() => onOpenTask(a.task)}>
                        <ChevronRight size={11} aria-hidden />
                        {a.task.title}
                      </button>
                    </span>
                  </li>
                ))}
                <li className="pip-act">
                  <Avatar userId={Number(project.createdBy) || 0} name={nameOf[project.createdBy] ?? ''} src={null} size="xs" />
                  <span className="pip-act-body">
                    <span className="pip-act-line">
                      {tProject('project.info.act_project_created', { name: nameOf[project.createdBy] ?? tTask('task.topbar.personal') })}
                      <em className="pip-act-at">{shortDateTime(project.createdAt)}</em>
                    </span>
                  </span>
                </li>
              </ul>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

/** 'M. D. (요일)' — 참조 시안의 활동 시각 표기와 같은 결. */
function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dow = ['일', '월', '화', '수', '목', '금', '토'][d.getDay()];
  return `${d.getMonth() + 1}. ${d.getDate()}. (${dow})`;
}
